import React, { useState } from 'react';
import { ProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { FaHome, FaChartLine, FaAndroid, FaFont, FaRobot, FaWrench, FaTerminal, FaCog, FaPencilAlt, FaSlidersH, FaBolt, FaThLarge, FaCalendar, FaSitemap, FaMusic, FaVideo, FaGamepad, FaBook, FaCoins } from 'react-icons/fa';
import 'react-pro-sidebar/dist/css/styles.css';
import './SidebarMenu.css';

/**
 * Renders the sidebar navigation used in the Twitch‑chatbot dashboard.
 *
 * @module components/SidebarMenu
 * @param {Object}   props
 * @param {string}   [props.backgroundColor='#5D3B9E'] Base HEX color to derive semi‑transparent backgrounds.
 * @param {Function} [props.setIsBlur=()=>{}]           Callback to blur the main content when the sidebar is open.
 * @param {Function} [props.openDialogComponent=()=>{}] Opens a modal dialog for configuration tasks.
 * @param {Function} [props.setMainViewComponent=()=>{}] Switches the main panel to a different React component.
 * @param {Function} [props.setMainViewHeader=()=>{}]    Updates the main panel heading text.
 * @returns {JSX.Element}
 */
const SidebarMenu = ({backgroundColor = '#5D3B9E', setIsBlur = () => {}, openDialogComponent = () => {}, setMainViewComponent = () => {}, setMainViewHeader = () => {}}) => {
    const [openSubMenu, setOpenSubMenu] = useState(null);
    /** Converts a HEX colour string to an RGBA string with the supplied opacity. */
    const hexToRgba = (hex, alpha) => `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, ${alpha})`;
    const colorScheme = {
        background75: hexToRgba(backgroundColor, 0.75),
        background50: hexToRgba(backgroundColor, 0.5),
        background25: hexToRgba(backgroundColor, 0.25)
    };

    /** Toggles visibility of a SubMenu */
    const handleSubMenuClick = (menuName) => {
        setOpenSubMenu(openSubMenu === menuName ? null : menuName);
    };
    /**
     * Handles leaf‑menu clicks without triggering the SubMenu toggle.
     * @param {React.MouseEvent<HTMLDivElement>} event
     * @param {string} componentName
     */
    const handleMenuItemClick = (event, componentName) => {
        event.stopPropagation();
        setMainViewComponent(componentName);
    };
    /**
     * Wrapper around h4 / h5 for consistent sizing in the sidebar.
     * @param {Object} props
     * @param {'main'|'sub'} [props.level='main']
     */
    const MenuLabel = ({ level = 'main', children }) => {
        const Tag = level === 'sub' ? 'h5' : 'h4';
        return (
            <Tag style={{ margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                {children}
            </Tag>
        );
    };

    return (
        <ProSidebar breakPoint="sm" className="custom-sidebar" width="200px" style={{ '--icon-background-color': colorScheme.background75 }}>
            <Menu iconShape="circle" style={{backgroundColor: colorScheme.background25, height: '100vh', borderRight: `2px solid ${colorScheme.background50}`}}>
                {/* Overview */}
                <MenuItem onClick={() => setMainViewComponent('dashboard')} icon={<FaHome />}>
                    <MenuLabel level="main">Dashboard</MenuLabel>
                </MenuItem>
                <MenuItem onClick={() => setMainViewComponent('streamstats')} icon={<FaChartLine />}>
                    <MenuLabel level="main">Statistics</MenuLabel>
                </MenuItem>
                {/* Bot Personality & Language */}
                <SubMenu title={<MenuLabel level="main">Persona & AI</MenuLabel>} icon={<FaAndroid />} open={openSubMenu === 'personaLang'} 
                    onClick={() => handleSubMenuClick('personaLang')}>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'aipresets')} icon={<FaRobot />}>
                        <MenuLabel level="sub">Personalities</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'personalitybuilder')} icon={<FaWrench />}>
                        <MenuLabel level="sub">Create Personality</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'keywords')} icon={<FaFont />}>
                        <MenuLabel level="sub">Keywords</MenuLabel>
                    </MenuItem>
                </SubMenu>
                {/* Automation */}
                <SubMenu title={<MenuLabel level="main">Automation</MenuLabel>} icon={<FaTerminal />} open={openSubMenu === 'automation'}
                    onClick={() => handleSubMenuClick('automation')}>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'systemcommands')}icon={<FaCog />}>
                        <MenuLabel level="sub">Built-in Commands</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'customcommands')} icon={<FaPencilAlt />}>
                        <MenuLabel level="sub">My Commands</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'macros')} icon={<FaSlidersH />}>
                        <MenuLabel level="sub">Macros</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'triggers')} icon={<FaBolt />}>
                        <MenuLabel level="sub">Triggers</MenuLabel>
                    </MenuItem>
                </SubMenu>
                {/* Variables */}
                <SubMenu title={<MenuLabel level="main">Variables</MenuLabel>} icon={<FaThLarge />} open={openSubMenu === 'variables'}
                    onClick={() => handleSubMenuClick('variables')}>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'systemvariables')} icon={<FaCog />}>
                        <MenuLabel level="sub">System Variables</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'customvariables')} icon={<FaPencilAlt />}>
                        <MenuLabel level="sub">Custom Variables</MenuLabel>
                    </MenuItem>
                </SubMenu>
                {/* Scheduling */}
                <MenuItem onClick={() => setMainViewComponent('calendar')} icon={<FaCalendar />}>
                    <MenuLabel level="main">Calendar</MenuLabel>
                </MenuItem>
                {/* Twitch */}
                <SubMenu title={<MenuLabel level="main">Twitch</MenuLabel>} icon={<FaVideo />} open={openSubMenu === 'twitchsettings'}
                    onClick={() => handleSubMenuClick('twitchsettings')}>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'twitch')} icon={<FaSlidersH />}>
                        <MenuLabel level="sub">Manage</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'viewerrewards')} icon={<FaCoins />}>
                        <MenuLabel level="sub">Rewards</MenuLabel>
                    </MenuItem>
                </SubMenu>
                {/* Integrations */}
                <SubMenu title={<MenuLabel level="main">Integrations</MenuLabel>} icon={<FaSitemap />} open={openSubMenu === 'integrations'}
                    onClick={() => handleSubMenuClick('integrations')}>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'spotify')} icon={<FaMusic />}>
                        <MenuLabel level="sub">Spotify Settings</MenuLabel>
                    </MenuItem>
                </SubMenu>
                {/* Games & Engagement */}
                <SubMenu title={<MenuLabel level="main">Games</MenuLabel>} icon={<FaGamepad />} open={openSubMenu === 'games'}
                    onClick={() => handleSubMenuClick('games')}>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'trivia')} icon={<FaBook />}>
                        <MenuLabel level="sub">Trivia</MenuLabel>
                    </MenuItem>
                    <MenuItem onClick={(e) => handleMenuItemClick(e, 'hangman')} icon={<FaBook />}>
                        <MenuLabel level="sub">Hangman</MenuLabel>
                    </MenuItem>
                </SubMenu>
            </Menu>
        </ProSidebar>
    );
};

export default SidebarMenu;
